// app/frontend/pages/Cities/CityIndex.tsx
import React, { useState, useEffect } from 'react';
import { router } from '@inertiajs/react';
import {
  Container,
  Title,
  TextInput,
  Select,
  Table,
  Card,
  Text,
  Group,
  Stack,
  Badge,
  ActionIcon,
  Box,
  Grid,
  Button,
  Flex,
  MantineProvider,
  createTheme,
} from '@mantine/core';
import { useDebouncedValue, useMediaQuery } from '@mantine/hooks';
import { Notifications, showNotification } from '@mantine/notifications';
import { IconSearch, IconEdit, IconFilter, IconTrash, IconCheck, IconAlertCircle } from '@tabler/icons-react';
import { StablePagination } from '../../../components/StablePagination';

import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';

interface City {
  id: number;
  name: string;
  area: {
    id: number;
    name: string;
  } | null;
  country: {
    id: number;
    name: string;
  } | null;
  continent: {
    id: number;
    name: string;
  } | null;
  events_count: number;
}

interface Country {
  id: number;
  name: string;
  continent_name: string;
}

interface Area {
  id: number;
  name: string;
  country_id: number;
  country_name: string;
}

interface Props {
  cities: City[];
  countries: Country[];
  areas: Area[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_count: number;
    per_page: number;
  };
  filters: {
    search: string;
    country_id: string;
    area_id: string;
  };
}

const theme = createTheme({
  primaryColor: 'orange',
  colors: {
    orange: [
      '#FEF5ED',
      '#FDE3D1',
      '#FCB693',
      '#FA9D73',
      '#F87F4D',
      '#F66A33',
      '#EA580C',
      '#C44B0A',
      '#9E3D08',
      '#783006',
    ],
  },
  fontFamily: 'Poppins, sans-serif',
  headings: { fontFamily: 'Poppins, sans-serif' },
});

export default function CitiesIndex({ cities, countries, areas, pagination, filters }: Props) {
  const [search, setSearch] = useState(filters.search);
  const [countryId, setCountryId] = useState(filters.country_id);
  const [areaId, setAreaId] = useState(filters.area_id);
  const [debouncedSearch] = useDebouncedValue(search, 500);
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Filter areas based on selected country
  const filteredAreas = countryId ? areas.filter((area) => area.country_id.toString() === countryId) : areas;

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (countryId) params.set('country_id', countryId);
    if (areaId) params.set('area_id', areaId);
    params.set('page', '1'); // Reset to first page when filtering

    router.get(
      `/admin/city-index?${params.toString()}`,
      {},
      {
        preserveState: true,
        preserveScroll: true,
        replace: true,
      },
    );
  }, [debouncedSearch, countryId, areaId]);

  // Clear area when country changes
  useEffect(() => {
    if (countryId && areaId) {
      const selectedArea = areas.find((area) => area.id.toString() === areaId);
      if (selectedArea && selectedArea.country_id.toString() !== countryId) {
        setAreaId('');
      }
    }
  }, [countryId]);

  const handleDelete = (cityId: number, cityName: string) => {
    if (confirm(`Are you sure you want to delete "${cityName}"? This action cannot be undone.`)) {
      router.delete(`/admin/city-index/${cityId}`, {
        onSuccess: () => {
          showNotification({
            color: 'green',
            title: 'City Deleted',
            message: `"${cityName}" was successfully deleted.`,
            icon: <IconCheck />,
            autoClose: 5000,
          });
          // No manual redirect needed - Inertia keeps you on index page
        },
        onError: (errors) => {
          showNotification({
            color: 'red',
            title: 'Delete Failed',
            message: errors.error || 'Failed to delete city.',
            icon: <IconAlertCircle />,
            autoClose: 5000,
          });
        },
      });
    }
  };

  const handlePageChange = (page: number) => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (countryId) params.set('country_id', countryId);
    if (areaId) params.set('area_id', areaId);
    params.set('page', page.toString());

    router.get(
      `/admin/city-index?${params.toString()}`,
      {},
      {
        preserveState: true,
        preserveScroll: true,
      },
    );
  };

  const handleEdit = (cityId: number) => {
    router.get(`/admin/city-index/${cityId}/edit`);
  };

  const clearFilters = () => {
    setSearch('');
    setCountryId('');
    setAreaId('');
  };

  const CityCard = ({ city }: { city: City }) => (
    <Card
      shadow="sm"
      padding="md"
      radius="md"
      withBorder
      style={{
        backgroundColor: '#171717',
        borderColor: '#404040',
      }}
    >
      <Stack gap="xs">
        <Group justify="space-between" align="flex-start">
          <div>
            <Text fw={600} size="lg" c="white">
              {city.name}
            </Text>
            <Text size="sm" c="dimmed">
              ID: {city.id} • {city.area?.name || 'No area'}
            </Text>
          </div>
          <Group gap="xs">
            <ActionIcon variant="light" onClick={() => handleEdit(city.id)} size="lg">
              <IconEdit size={16} />
            </ActionIcon>
            <ActionIcon variant="light" color="red" onClick={() => handleDelete(city.id, city.name)} size="lg">
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        </Group>

        <Group justify="apart">
          <div>
            <Text size="sm" c="dimmed">
              Country
            </Text>
            <Text size="sm" c="white">
              {city.country?.name || 'No country'}
            </Text>
          </div>
          <div>
            <Text size="sm" c="dimmed">
              Continent
            </Text>
            <Text size="sm" c="white">
              {city.continent?.name || 'No continent'}
            </Text>
          </div>
          <div>
            <Text size="sm" c="dimmed">
              Events
            </Text>
            <Badge variant="light" color="orange">
              {city.events_count}
            </Badge>
          </div>
        </Group>
      </Stack>
    </Card>
  );

  return (
    <MantineProvider theme={theme}>
      <Notifications />
      <Container size="xl" py="md">
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between">
            <Title order={1} className="rubik-header">
              Cities ({pagination.total_count})
            </Title>
          </Group>

          {/* Filters */}
          <Card padding="md" style={{ backgroundColor: '#171717' }}>
            <Stack gap="md">
              <Group>
                <IconFilter size={20} color="orange" />
                <Text fw={500} c="white">
                  Filters
                </Text>
                {(search || countryId || areaId) && (
                  <Button variant="light" size="xs" onClick={clearFilters}>
                    Clear all
                  </Button>
                )}
              </Group>

              <Grid>
                <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                  <TextInput
                    placeholder="Search cities, areas, countries..."
                    leftSection={<IconSearch size={16} />}
                    value={search}
                    onChange={(e) => setSearch(e.currentTarget.value)}
                  />
                </Grid.Col>

                <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                  <Select
                    placeholder="Select country"
                    data={countries.map((country) => ({
                      value: country.id.toString(),
                      label: `${country.name} (${country.continent_name})`,
                    }))}
                    value={countryId}
                    onChange={(value) => setCountryId(value || '')}
                    clearable
                    searchable
                  />
                </Grid.Col>

                <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                  <Select
                    placeholder="Select area"
                    data={filteredAreas.map((area) => ({
                      value: area.id.toString(),
                      label: `${area.name} (${area.country_name})`,
                    }))}
                    value={areaId}
                    onChange={(value) => setAreaId(value || '')}
                    clearable
                    searchable
                    disabled={!countryId}
                  />
                </Grid.Col>
              </Grid>
            </Stack>
          </Card>

          {/* Cities Content - Responsive */}
          {isMobile ? (
            /* Mobile Cards */
            <Stack gap="md" style={{ backgroundColor: '#171717' }}>
              {cities.map((city) => (
                <CityCard key={city.id} city={city} />
              ))}
            </Stack>
          ) : (
            /* Desktop Table */
            <Box>
              <Table
                highlightOnHover
                striped
                style={{
                  marginTop: 20,
                  color: '#f2f2f2',
                  minWidth: 600,
                  borderSpacing: '0 12px',
                  borderCollapse: 'separate',
                }}
                styles={{
                  root: {
                    width: '100%',
                    tableLayout: 'fixed',
                  },
                  td: {
                    padding: '22px 18px',
                    verticalAlign: 'middle',
                    backgroundColor: '#171717',
                    color: '#f2f2f2',
                  },
                  th: {
                    padding: '12px 8px',
                    color: '#f2f2f2',
                  },
                  tr: {
                    '&:nth-of-type(even)': {
                      backgroundColor: '#17171750',
                    },
                  },
                }}
              >
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>ID</Table.Th>
                    <Table.Th>City</Table.Th>
                    <Table.Th>Area</Table.Th>
                    <Table.Th>Country</Table.Th>
                    <Table.Th>Continent</Table.Th>
                    <Table.Th>Events</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {cities.map((city) => (
                    <Table.Tr key={city.id}>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {city.id}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text fw={500}>{city.name}</Text>
                      </Table.Td>
                      <Table.Td>{city.area?.name || 'No area'}</Table.Td>
                      <Table.Td>{city.country?.name || 'No country'}</Table.Td>
                      <Table.Td>{city.continent?.name || 'No continent'}</Table.Td>
                      <Table.Td>
                        <Badge variant="light" color="orange">
                          {city.events_count}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <ActionIcon variant="light" onClick={() => handleEdit(city.id)}>
                          <IconEdit size={16} />
                        </ActionIcon>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Box>
          )}

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <Flex justify="center">
              <StablePagination
                total={pagination.total_pages}
                value={pagination.current_page}
                onChange={handlePageChange}
              />
            </Flex>
          )}

          {/* No results */}
          {cities.length === 0 && (
            <Card padding="xl">
              <Text ta="center" c="dimmed">
                No cities found matching your criteria.
              </Text>
            </Card>
          )}
        </Stack>
      </Container>
    </MantineProvider>
  );
}
